import React, { useState, useEffect, useContext } from "react";
import {
	Box,
	Typography,
	Button,
	List,
	ListItem,
	ListItemText,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	alpha,
	CircularProgress,
	getLuminance,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import {
	IconCalendarEvent,
	IconTrash,
	IconArrowLeft,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../contexts/UserContext.js";
import AsyncStorage from "@react-native-async-storage/async-storage";
import ConnectionsClient from "../../services/ConnectionsClient.js";
import { CreatorContext } from "../../contexts/CreatorContext.js";

function Appointments() {
	const { t } = useTranslation();
	const [appointments, setAppointments] = useState([]);
	const [openDialog, setOpenDialog] = useState(false); // Controlar la apertura/cierre de la modal
	const navigate = useNavigate();
	const { basePath } = useParams();

	const [selectedAppointment, setSelectedAppointment] = useState(null);

	const [deleting, setDeleting] = useState(false);

	const [end, setEnd] = useState(false);
	const limit = 10; // Supón que tienes un límite definido
	const offset = 0; // Supón que tienes un offset definido

	const { user } = useContext(UserContext); // Usamos el contexto para obtener el usuario y getUser
	const [loading, setLoading] = useState(false);

	const { creator } = useContext(CreatorContext);

	useEffect(() => {
		if (user /*&& !loading*/ && !end) {
			(async () => {
				setLoading(true);

				try {
					// Si el token es necesario, lo recuperas de AsyncStorage
					const token = await AsyncStorage.getItem("token");

					// Configura los headers si necesitas el token
					const headers = token ? { authorization: token } : {};

					// Llamada a la API con ConnectionsClient
					let res = [];

					if (!creator.isCreator) {
						res = await ConnectionsClient.get("/appointments", {
							params: {
								limit: limit,
								offset: offset,
							},
							headers: headers,
						});
					} else {
						res = await AsyncStorage.getItem("appointments");

						if (res === null) {
							res = {
								data: [],
							};
						} else {
							res = {
								data: JSON.parse(res),
							};
						}
						console.log(res);
					}

					// Manejar la respuesta y actualizar el estado
					if (offset === 0) {
						setAppointments(res.data);
					} else {
						setAppointments((prevAppointments) => [
							...prevAppointments,
							...res.data,
						]);
					}

					// Si hay menos datos que el límite, es el final
					if (res.data.length < limit) {
						setEnd(true);
					}
				} catch (error) {
					console.error("Error fetching appointments:", error);
				} finally {
					setLoading(false);
				}
			})();
		}
	}, [end, user, creator.isCreator]);

	const deleteAppointment = async () => {
		try {
			setDeleting(true);

			let auxAppointments = [];
			if (!creator.isCreator) {
				auxAppointments = [...appointments];

				const token = await AsyncStorage.getItem("token");
				const headers = token
					? { authorization: token, "x-wc-call": true }
					: {};

				await ConnectionsClient.delete("/delete-appointment", {
					data: { appointmentId: selectedAppointment.id },
					headers: headers,
				});
			} else {
				auxAppointments = await AsyncStorage.getItem("appointments");
				auxAppointments = JSON.parse(auxAppointments);
				let index = auxAppointments.findIndex(
					(appointment) => appointment.id === selectedAppointment.id
				);

				if (index > -1) {
					auxAppointments.splice(index, 1);
					await AsyncStorage.setItem(
						"appointments",
						JSON.stringify(auxAppointments)
					);
				}
			}

			let index = auxAppointments.findIndex(
				(appointment) => appointment.id === selectedAppointment.id
			);
			if (index > -1) {
				auxAppointments.splice(index, 1);
			}

			setAppointments([...auxAppointments]);

			setOpenDialog(false);
		} catch (error) {
			console.log(error);
			// show error
		}
		setDeleting(false);
	};

	// Abrir la modal de confirmación
	const handleOpenDialog = (appointment) => {
		setSelectedAppointment(appointment); // Guardamos el índice de la cita a cancelar
		setOpenDialog(true); // Abrimos la modal
	};

	// Cerrar la modal de confirmación
	const handleCloseDialog = () => {
		setOpenDialog(false);
		setSelectedAppointment(null);
	};

	// Confirmar cancelación
	const handleConfirmCancel = () => {
		console.log(selectedAppointment);
		if (selectedAppointment !== null) {
			deleteAppointment();
		}
	};
	const formatDate = (dateString) => {
		const options = {
			weekday: "short",
			year: "numeric",
			month: "short",
			day: "numeric",
		};
		const date = new Date(dateString);
		return date.toLocaleDateString("es-ES", options);
	};

	const formatTime = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<Box
			sx={{
				padding: { xs: "0px 16px", sm: "0px 30px" },
				backgroundColor: creator?.backgroundColor || "#ffffff",
				color: creator?.fontColor || "#000000",
				minHeight: "calc(100vh - 90px)",
			}}
		>
			<IconButton
				onClick={() => navigate(`/${basePath}/`)} // Navega hacia atrás
				sx={{
					marginBottom: "20px",
					marginLeft: "-8px",
					color: alpha(creator?.fontColor ?? "#000000", 0.8),
				}} // Espacio debajo de la flecha
			>
				<IconArrowLeft />
			</IconButton>
			<Box>
				<Typography
					variant="h4"
					gutterBottom
					sx={{
						fontSize: "16px",
						fontWeight: 600,
						marginBottom: 2,
					}}
				>
					{t("yourAppointments")}
				</Typography>
				{appointments.length > 0 ? (
					<Box
						sx={{
							maxHeight: "calc(100% - 120px)",
							paddingBottom: "80px",
							overflowY: "auto",
						}}
					>
						<List>
							{appointments.map((appointment, index) => (
								<ListItem
									key={index}
									sx={{
										display: "flex",
										justifyContent: "space-between",
										alignItems: "center",
										borderBottom:
											"1px solid " +
											alpha(creator?.fontColor ?? "#000000", 0.1),
										padding: "10px 0",
									}}
								>
									<ListItemText
										primary={
											<>
												<Box sx={{ display: "flex" }}>
													<Typography
														sx={{ fontSize: "14px", fontWeight: "bold" }}
													>
														{appointment?.service?.title}
													</Typography>

													{appointment?.service?.serviceOptions &&
														appointment?.service?.serviceOptions.length > 0 && (
															<Typography
																sx={{
																	fontSize: "14px",
																	fontWeight: "bold",
																	ml: "4px",
																}} // Aquí añadimos el margen izquierdo
															>
																-{" "}
																{appointment?.service?.serviceOptions[0].title}
															</Typography>
														)}
												</Box>
												<Box sx={{ display: "flex" }}>
													{appointment?.service?.serviceOptions &&
													appointment?.service?.serviceOptions.length > 0 ? (
														<Typography
															sx={{
																fontSize: "14px",
																color: alpha(
																	creator?.fontColor ?? "#000000",
																	0.8
																),
															}} // Aquí añadimos el margen izquierdo
														>
															{t("price")}:{" "}
															{new Intl.NumberFormat(creator.language ?? "en", {
																style: "currency",
																currency: creator.currency ?? "eur",
															}).format(
																appointment?.service?.serviceOptions[0].price
															)}
														</Typography>
													) : (
														<Typography
															color="textSecondary"
															sx={{
																color: alpha(
																	creator?.fontColor ?? "#000000",
																	0.8
																),
															}}
														>
															{t("price")}:{" "}
															{new Intl.NumberFormat(creator.language ?? "en", {
																style: "currency",
																currency: creator.currency ?? "eur",
															}).format(appointment?.service?.price)}
														</Typography>
													)}
												</Box>
												<Box
													sx={{
														display: "flex",
														marginTop: 1,
														alignItems: "center",
													}}
												>
													<IconCalendarEvent size={14} />
													<Typography
														variant="body2"
														sx={{
															marginLeft: 1,
															color: alpha(
																creator?.fontColor ?? "#000000",
																0.8
															),
														}}
													>
														{formatDate(appointment.date)} {t("at")}{" "}
														{formatTime(appointment.date)}
													</Typography>
												</Box>
												<Typography
													sx={{
														marginTop: 1,
														fontSize: "14px",
														color: alpha(creator?.fontColor ?? "#000000", 0.8),
													}}
												>
													{t("withWorker", {
														workerName: appointment?.worker?.name,
													})}
												</Typography>
											</>
										}
									/>
									<IconButton
										color="primary"
										onClick={() => handleOpenDialog(appointment)}
									>
										<IconTrash size={18} />
									</IconButton>
								</ListItem>
							))}
						</List>
					</Box>
				) : loading ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							padding: 2,
						}}
					>
						<CircularProgress size={12} />
					</Box>
				) : (
					<Typography>{t("noAppointments")}</Typography>
				)}
			</Box>
			{/* Botones para volver atrás y crear una nueva cita */}
			<Box
				sx={{
					position: "fixed",
					width: "100%",
					bottom: 0,
					right: 0,
					padding: "20px",
					textAlign: "right",
					backgroundColor: creator?.backgroundColor || "#ffffff",
				}}
			>
				<Button
					variant="contained"
					color="primary"
					onClick={() => navigate(`/${basePath}/`)}
					sx={{ width: { xs: "calc(100% - 40px)", sm: "auto" } }}
				>
					{t("newAppointment")}
				</Button>
			</Box>

			{/* Modal de confirmación */}
			<Dialog open={openDialog} onClose={handleCloseDialog}>
				<DialogTitle
					sx={{
						backgroundColor: creator?.backgroundColor || "#ffffff",
						color: creator?.fontColor || "#000000",
					}}
				>
					{t("confirmCancellation")}
				</DialogTitle>
				<DialogContent
					sx={{
						backgroundColor: creator?.backgroundColor || "#ffffff",
						color: creator?.fontColor || "#000000",
					}}
				>
					<Typography>{t("cancellationWarning")}</Typography>
				</DialogContent>
				<DialogActions
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						padding: "16px",
						flexDirection: {
							xs: "column", // Para pantallas pequeñas (se apilan verticalmente)
							sm: "row", // Para pantallas grandes (se alinean horizontalmente)
						},
						backgroundColor: creator?.backgroundColor || "#ffffff",
						color: creator?.fontColor || "#000000",
						gap: 1, // Añadir espacio entre botones
					}}
				>
					<Button
						variant="contained"
						color="primary"
						onClick={() => {
							setOpenDialog(false);
							setSelectedAppointment(null);
						}}
						sx={{ width: { xs: "100%", sm: "auto" } }}
						disabled={deleting}
					>
						{t("keepAppointment")}
					</Button>
					<Button
						variant="outlined"
						onClick={handleConfirmCancel}
						color="primary"
						sx={{
							width: { xs: "100%", sm: "auto" },
							color: deleting ? "transparent!important" : undefined,
							"&.Mui-disabled": {
								opacity: 0.5,
								color: deleting
									? "transparent!important"
									: getLuminance(creator?.mainColor ?? "#ffffff") >= 0.5
									? "#000000"
									: "#FFFFFF", // Texto de los botones primarios
								backgroundColor: creator?.mainColor ?? "#43BD98",
							},
						}}
						disabled={deleting}
					>
						{t("cancelAppointment")}
						{deleting && (
							<CircularProgress
								size={12}
								sx={{ position: "absolute", top: 16, color: "white" }}
							/>
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default Appointments;
